import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    transform: translateY(-50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease-out;
  padding: 1rem;
`;

export const ModalContent = styled.div`
  background: ${(props) => props.theme.colors.background};
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  animation: ${slideIn} 0.3s ease-out;
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid
    ${(props) => props.theme.colors.palet.institutional13};
`;

export const ModalTitle = styled.h2`
  color: ${(props) => props.theme.colors.palet.institutional3};
  font-family: 'Unimed-Sans-SemiBold';
  font-size: 1.5rem;
  margin: 0;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 2rem;
  color: ${(props) => props.theme.colors.text.grayishToLightGray};
  cursor: pointer;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => props.theme.colors.palet.institutional13};
    color: ${(props) => props.theme.colors.text.normal};
  }
`;

export const ModalBody = styled.div`
  padding: 2rem;
`;

export const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

export const Label = styled.label`
  display: block;
  color: ${(props) => props.theme.colors.text.normal};
  font-family: 'Unimed-Sans-SemiBold';
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

interface ActionButtonProps {
  variant: 'primary' | 'secondary';
}

export const ActionButton = styled.button<ActionButtonProps>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-family: 'Unimed-Sans-SemiBold';
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid;
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  ${(props) =>
    props.variant === 'primary'
      ? `
    background: ${props.theme.colors.palet.institutional};
    color: white;
    border-color: ${props.theme.colors.palet.institutional};

    &:hover:not(:disabled) {
      background: ${props.theme.colors.palet.institutional2};
      border-color: ${props.theme.colors.palet.institutional2};
    }

    &:disabled {
      background: ${props.theme.colors.text.grayishToLightGray};
      border-color: ${props.theme.colors.text.grayishToLightGray};
      cursor: not-allowed;
    }
  `
      : `
    background: transparent;
    color: ${props.theme.colors.text.normal};
    border-color: ${props.theme.colors.palet.institutional13};

    &:hover {
      background: ${props.theme.colors.palet.institutional13};
    }
  `}

  @media (max-width: 480px) {
    width: 100%;
  }
`;

export const ShareModalTitle = styled.h1`
  font-size: 1.25rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 1rem;
`;

export const ShareModalAttention = styled.span`
  color: ${(props) => props.theme.colors.palet.institutional};
  font-weight: bold;
  display: block;
  margin-bottom: 1rem;
`;

export const FullActionButton = styled(ActionButton)`
  width: 100%;
  margin-top: 2rem;
`;

export const SubtitleCentered = styled.span`
  width: 100%;
  text-align: center;
  display: block;
  font-weight: bold;
  font-size: 0.75rem;
  color: ${(props) => props.theme.colors.text.grayishToWhite};
`;

export const ShareCodeContainer = styled.div`
  border: 1px solid ${(props) => props.theme.colors.text.lightGrayToBlack};
  border-radius: 4px;
  padding: 0.5rem;
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  align-items: center;
  margin-top: 0.75rem;
`;

export const ShareCode = styled.span`
  color: ${(props) => props.theme.colors.palet.institutional};
  font-size: 1.36rem;
  font-weight: bold;
  flex-grow: 1;
  text-align: center;
`;

export const ShareActionButton = styled.button`
  border: none;
  background: none;
`;

export const ShareActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;
